import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import {
  Fragment,
  useState,
  type CSSProperties,
  type PropsWithChildren,
} from 'react';
import styled from 'styled-components';

import { CardPackQuality } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandS,
  BrandXS,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { BottomArrow } from '@sorare/core/src/atoms/ui/bezels/BottomArrow';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { isType } from '@sorare/core/src/lib/gql';

import { VoucherPaymentDialog } from '#src/components/commonCard/BuyPacks/GemsPack/VoucherPaymentDialog/index.tsx';

import type { BundledPurchasableItem_bundledPurchasableItem } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 2 })`
  position: relative;
  padding: var(--double-unit) var(--unit) 0;
  justify-content: space-between;
  text-align: center;
  cursor: pointer;
`;

const Container = styled(Vertical).attrs({ center: true, gap: 2 })`
  flex-grow: 1;
  justify-content: flex-start;
`;

const ItemPicture = styled(Img)`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  object-fit: contain;
  &.isGold {
    width: calc(6 * var(--unit));
    height: calc(6 * var(--unit));
    filter: drop-shadow(0px 0px 16.8px rgba(251, 255, 26, 0.35));
  }
`;

const ItemDivider = styled.div`
  height: 1px;
  width: calc(6 * var(--unit));
  background-color: var(--c-nd-150);
`;

const OldPrice = styled(LabelS)`
  text-decoration: line-through;
`;

const StyledButton = styled(Button)`
  align-self: center;
  &.compact {
    padding: var(--unit) var(--intermediate-unit);
    height: unset;
  }
`;

type Props = {
  bundledPurchasableItem: BundledPurchasableItem_bundledPurchasableItem;
  onPackPurchased: (offerId: string) => void;
  voucherId: string;
  borderGradient?: string;
};

export const BundledPurchasableItem = ({
  bundledPurchasableItem,
  onPackPurchased,
  voucherId,
  borderGradient,
  children,
}: PropsWithChildren<Props>) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const [dialogOpened, setDialogOpened] = useState(false);

  const {
    id: purchasableItemId,
    price: bundledPrice,
    oldPrice: bundledOldPrice,
    purchasableItems,
  } = bundledPurchasableItem;

  const price = toMonetaryAmount(bundledPrice);
  const { main } = useAmountWithConversion({ monetaryAmount: price });
  const oldPrice = bundledOldPrice ? toMonetaryAmount(bundledOldPrice) : null;
  const { main: oldPriceLabel } = useAmountWithConversion({
    monetaryAmount: oldPrice,
  });

  if (price.isZero || purchasableItems.length === 0) return null;

  const hasOldPrice = !!oldPrice && !oldPrice.isZero;
  return (
    <>
      <ButtonBase
        onClick={() => {
          setDialogOpened(true);
        }}
      >
        <BottomArrow borderGradient={borderGradient}>
          <Root>
            {children}
            <Container>
              {purchasableItems.map((item, index) => {
                let itemPictureUrl = '';
                let label = '';
                let isGold = false;
                if (isType(item, 'InGameCurrencyPurchasableItem')) {
                  itemPictureUrl = item.lowResPictureUrl || '';
                  label = `x${item.totalAmount}`;
                }
                if (isType(item, 'CardShardsPurchasableItem')) {
                  itemPictureUrl = item.lowResPictureUrl || '';
                  label = `x${item.quantity}`;
                }
                if (isType(item, 'PackConfigPurchasableItem')) {
                  isGold = item.cardPack.quality === CardPackQuality.GOLD;
                  itemPictureUrl = item.cardPack.pictureUrl;
                  // Ugly but get only silver/gold from title
                  label = `x1 ${isGold ? 'Gold' : ''}`;
                }
                return (
                  <Fragment key={item.slug}>
                    {index > 0 && <ItemDivider />}
                    <Horizontal gap={0.5} center>
                      {!!itemPictureUrl && (
                        <ItemPicture
                          src={itemPictureUrl}
                          alt=""
                          className={classnames({ isGold })}
                        />
                      )}
                      {isGold ? (
                        <BrandS
                          style={
                            {
                              '--text-gradient-color':
                                'linear-gradient(217.81deg, #FFFFFF 1.37%, #FCFF63 36.66%, #C26900 101.57%)',
                            } as CSSProperties
                          }
                          className="text-gradient-color whitespace-nowrap"
                        >
                          {label}
                        </BrandS>
                      ) : (
                        <BrandXS>{label}</BrandXS>
                      )}
                    </Horizontal>
                  </Fragment>
                );
              })}
            </Container>

            <StyledButton
              asSpan
              size="compact"
              color={hasOldPrice ? 'tertiary' : 'primary'}
            >
              {hasOldPrice ? (
                <Vertical gap={0.5} center>
                  <OldPrice color="var(--c-nd-400)">{oldPriceLabel}</OldPrice>
                  <LabelM bold>{main}</LabelM>
                </Vertical>
              ) : (
                <span>{main}</span>
              )}
            </StyledButton>
          </Root>
        </BottomArrow>
      </ButtonBase>
      <VoucherPaymentDialog
        open={dialogOpened}
        onClose={() => setDialogOpened(false)}
        purchasableItemId={purchasableItemId}
        voucherId={voucherId}
        price={price}
        onPackPurchased={onPackPurchased}
      />
    </>
  );
};

BundledPurchasableItem.fragments = {
  bundledPurchasableItem: gql`
    fragment BundledPurchasableItem_bundledPurchasableItem on BundledPurchasableItem {
      slug
      id
      price {
        ...useMonetaryAmount_monetaryAmount
      }
      oldPrice {
        ...useMonetaryAmount_monetaryAmount
      }
      purchasableItems {
        slug
        ... on PackConfigPurchasableItem {
          slug
          cardPack {
            slug
            title
            pictureUrl
            quality
          }
        }
        ... on InGameCurrencyPurchasableItem {
          slug
          lowResPictureUrl: pictureUrl(derivative: LOW_RES)
          totalAmount
          currency
        }
        ... on CardShardsPurchasableItem {
          slug
          lowResPictureUrl: pictureUrl(derivative: LOW_RES)
          quantity
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<BundledPurchasableItem_bundledPurchasableItem>,
};
